import { supabase } from '@/lib/supabase'
import type { DashboardData } from './CommissionerPage'

/* Builds DashboardData from the schema in supabase/migrations. The interface is
   owned by CommissionerPage and unchanged; this file only shapes the query.

     poolName             pools.name
     weekLabel            weeks.label
     gamesThisWeek        count(games) for the week, excluding 'cancelled'
     locksAt              weeks.picks_lock_at
     memberLimit          pools.member_limit  (nullable -- see below)
     inviteUrl            built from pools.invite_code
     members[].id         commissioner_week_roster.user_id
     members[].name       commissioner_week_roster.display_name
     members[].picksMade  commissioner_week_roster.picks_made
     members[].lockSet    commissioner_week_roster.lock_set
     members[].rulesAcked commissioner_week_roster.rules_acked
     members[].balance    pool_ledger.net, sign-flipped
     standings[]          season_standings

   WEEK SELECTION. The week is pinned by week_number and reached through the
   pool's season_id (weeks is unique on (season_id, week_number)). It is
   deliberately NOT chosen by sorting on picks_lock_at: that column is a one-way
   ratchet set by trg_games_recompute_week_lock and is null for weeks whose
   kickoffs are not published yet, so ordering by it silently picks the wrong
   week late in the season.

   PICKS HAVE NO week_id. They reach weeks through games: picks.game_id ->
   games.id, then games.week_id. That join lives inside commissioner_week_roster
   (0011) rather than here, because RLS makes it unreadable from the client:
   `picks_select_pool_after_lock` only exposes another member's picks once the
   week has locked, and `pool_rules_ack_select_own` is self-only. The function
   is security definer and returns counts and booleans exclusively -- never
   picked_team -- so it answers "who still needs to pick" without leaking who
   picked what before lock. */

/* pools.member_limit exists but is nullable, and null means unlimited rather
   than unknown. The header renders "N of LIMIT" and the footer renders
   "LIMIT - N spots left", neither of which has a sensible unlimited form, so an
   unlimited pool falls back to the free-tier cap. */
const DEFAULT_MEMBER_LIMIT = 12

export class CommissionerDataUnavailable extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'CommissionerDataUnavailable'
  }
}

export async function fetchDashboardData(
  poolId: string,
  weekNumber: number,
): Promise<DashboardData> {
  const { data: pool, error: poolError } = await supabase
    .from('pools')
    .select('id, name, season_id, member_limit, invite_code, entry_fee_amount')
    .eq('id', poolId)
    .single()
  if (poolError) throw poolError

  const { data: week, error: weekError } = await supabase
    .from('weeks')
    .select('id, label, picks_lock_at')
    .eq('season_id', pool.season_id)
    .eq('week_number', weekNumber)
    .single()
  if (weekError) throw weekError

  /* picks_lock_at is nullable by design -- weeks 16-18 have no published
     kickoffs until late, and recompute_week_lock leaves the column alone rather
     than inventing one. locksAt is a non-null string driving a live countdown,
     so there is nothing honest to put here. */
  if (!week.picks_lock_at) {
    throw new CommissionerDataUnavailable(
      `Week ${weekNumber} has no picks_lock_at yet: no game in it has a published kickoff.`,
    )
  }

  const { count: gameCount, error: gamesError } = await supabase
    .from('games')
    .select('id', { count: 'exact', head: true })
    .eq('week_id', week.id)
    .neq('status', 'cancelled')
  if (gamesError) throw gamesError

  /* One call covers the whole roster: the function joins profiles itself, so
     there is no separate pool_members or profiles query here. It raises 42501
     for a caller who is not the pool's commissioner. */
  const { data: roster, error: rosterError } = await supabase.rpc('commissioner_week_roster', {
    p_pool_id: poolId,
    p_week_id: week.id,
  })
  if (rosterError) throw rosterError

  /* pool_ledger only has rows where entry_fee_amount is set and positive, so a
     free pool legitimately returns nothing and every balance is 0.

     SIGN FLIP. The view's `net` is winnings - paid_in, i.e. positive means the
     pool owes the member. MemberStatus.balance is documented the other way
     round ("positive = owed to the pool"), so it is negated here.

     Note what `paid_in` actually is: coalesce(entry_fee_amount, 0) for every
     member unconditionally. The schema records no payments received, so this is
     amount DUE, not amount outstanding -- a member who has already settled with
     the commissioner still reads as owing until they win money back. */
  const balanceByUser = new Map<string, number>()
  if (pool.entry_fee_amount !== null && Number(pool.entry_fee_amount) > 0) {
    const { data: ledgerRows, error: ledgerError } = await supabase
      .from('pool_ledger')
      .select('user_id, net')
      .eq('pool_id', poolId)
    if (ledgerError) throw ledgerError
    for (const row of ledgerRows) {
      if (row.user_id) balanceByUser.set(row.user_id, -Number(row.net ?? 0))
    }
  }

  const { data: standingRows, error: standingsError } = await supabase
    .from('season_standings')
    .select('display_name, total_points, weekly_wins, season_rank')
    .eq('pool_id', poolId)
    .order('season_rank', { nullsFirst: false })
  if (standingsError) throw standingsError

  return {
    poolName: pool.name,
    weekLabel: week.label,
    gamesThisWeek: gameCount ?? 0,
    locksAt: week.picks_lock_at,
    memberLimit: pool.member_limit ?? DEFAULT_MEMBER_LIMIT,
    inviteUrl: `${window.location.origin}/join/${pool.invite_code}`,
    members: (roster ?? []).map((row) => ({
      id: row.user_id,
      name: row.display_name,
      picksMade: row.picks_made,
      lockSet: row.lock_set,
      rulesAcked: row.rules_acked,
      balance: balanceByUser.get(row.user_id) ?? 0,
    })),
    standings: standingRows.map((row) => ({
      name: row.display_name ?? 'Unknown member',
      points: Number(row.total_points ?? 0),
      weeksWon: Number(row.weekly_wins ?? 0),
    })),
  }
}
